import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";

import HttpManager from "../../services/HttpManager";
import { RootState } from "../../globals";
import { getAccessPermissions } from "../../common";
import TarjetaAprobacion, { LineasAprobacion } from "./tarjetaAprobacion";
import { AprobacionCreada, AprobacionKind, AprobacionPreview, MIN_MOTIVO } from "./tipos";

/*
	«Enviar a Aprobación» (CONTRATO_PROMO_EN_CAMINO §5, fase 2). Una sola ventana para todas las acciones que Caja no puede hacer sola:
	llama POST aprobaciones/_preview con { kind, data } (data = el cuerpo que enviaría la pantalla a su endpoint de siempre), enseña lo que
	va a pasar con los mismos renglones de la tarjeta de WappTeam, pide el motivo (10 letras o más) y manda POST aprobaciones.
	Si quien lo pide ya puede hacerlo directo (`canDoDirect`), el botón dice «Confirmar» y el servidor lo aplica como siempre (`aplicado`).
	409 = la cuenta cambió desde la vista previa: se vuelve a pedir la vista previa y se enseña lo nuevo antes de reenviar.
*/

export interface PedirAprobacion {
	kind: AprobacionKind;
	/** Título de la ventana, en Title Case («Eximir Recargo»). */
	titulo: string;
	/** El mismo cuerpo que la pantalla enviaría hoy a su endpoint. */
	data: Record<string, any>;
	/** Texto que la pantalla ya pidió (explicación, motivo, nota): llega escrito en el campo Motivo. */
	motivoInicial?: string;
	/** El servidor la aplicó o la dejó Por Aprobar: la pantalla recarga lo suyo. */
	onDone?: (r: AprobacionCreada) => void;
	/** Se cerró la ventana (después de Enviar o de Atrás). */
	onCerrar?: (r: AprobacionCreada | null) => void;
}

const http = HttpManager.getInstance();

const errorDe = (e: any): string => {
	const d = e?.response?.data;
	if (d?.errors && typeof d.errors === "object") {
		const v = d.errors[Object.keys(d.errors)[0]];
		if (v) return Array.isArray(v) ? v[0] : String(v);
	}
	return d?.message || d?.error || e?.message || "No se pudo completar. Intenta otra vez.";
};

export const EnviarAprobacion: React.FC<PedirAprobacion & { campusId: number }> = ({ campusId, kind, titulo, data, motivoInicial, onDone, onCerrar }) => {
	const [prev, setPrev] = useState<AprobacionPreview | null>(null);
	const [cargando, setCargando] = useState<boolean>(true);
	const [motivo, setMotivo] = useState<string>(motivoInicial || "");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [hecha, setHecha] = useState<AprobacionCreada | null>(null);

	const revisar = useCallback(async (aviso?: string) => {
		try {
			setCargando(true);
			setError(aviso || null);
			setPrev((await http.aprobacionesService.previewAprobacion(campusId, { kind, data })).data);
		} catch (e) {
			setPrev(null);
			setError(errorDe(e));
		} finally {
			setCargando(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, kind]);
	useEffect(() => { revisar(); }, [revisar]);

	const cerrar = () => onCerrar?.(hecha);
	const enviar = async () => {
		if (!prev || busy) return;
		try {
			setBusy(true);
			setError(null);
			const r = (await http.aprobacionesService.crearAprobacion(campusId, { kind, data, reason: motivo.trim(), previewHash: prev.previewHash })).data;
			onDone?.(r);
			if (r.result === "aplicado") { onCerrar?.(r); return; }
			setHecha(r);
		} catch (e: any) {
			if (e?.response?.status === 409) await revisar(errorDe(e));
			else setError(errorDe(e));
		} finally {
			setBusy(false);
		}
	};

	const directo = !!prev?.canDoDirect;
	const listo = !!prev && !prev.blocked && motivo.trim().length >= MIN_MOTIVO;

	return (
		<Modal className="waModulo" show onHide={cerrar} size="lg" centered scrollable fullscreen="sm-down" style={{ zIndex: 2000 }}>
			<Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>{titulo}</Modal.Title></Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 14 }}>
				{hecha ? (
					<>
						<div style={{ fontWeight: 600, color: "var(--wa-acento-texto)" }}>{hecha.text}</div>
						{hecha.approval ? <div style={{ marginTop: 12 }}><TarjetaAprobacion a={hecha.approval} /></div> : null}
					</>
				) : (
					<>
						{error ? <div role="alert" className="alert alert-danger" style={{ marginBottom: 10 }}>{error}</div> : null}
						{cargando ? <div className="loader" /> : prev ? (
							<>
								<div style={{ border: "1px solid var(--wa-filo)", borderRadius: 8, padding: "8px 12px", background: "var(--wa-superficie)" }}>
									<div style={{ fontWeight: 700 }}>Qué Va a Pasar</div>
									{prev.lines.length ? <LineasAprobacion lines={prev.lines} /> : <div>{prev.summary}</div>}
								</div>
								{prev.blocked ? <div role="alert" style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", color: "var(--wa-oro)", borderRadius: 8, padding: "8px 10px", marginTop: 10, fontWeight: 600 }}>{prev.blocked}</div> : null}
								<div style={{ fontWeight: 700, margin: "14px 0 4px" }}>Motivo</div>
								<textarea className="form-control" rows={3} style={{ fontSize: 16 }} value={motivo} disabled={busy} onChange={(e) => setMotivo(e.target.value)} />
								<div style={{ fontSize: 12, marginTop: 4, color: motivo.trim().length >= MIN_MOTIVO ? "var(--wa-apagado)" : "var(--wa-oro)" }}>{motivo.trim().length}/{MIN_MOTIVO}</div>
							</>
						) : null}
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{hecha ? (
					<button type="button" className="btn btn-primary" onClick={cerrar}>Cerrar</button>
				) : (
					<>
						<button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={cerrar}>Atrás</button>
						<button type="button" className="btn btn-primary" disabled={busy || cargando || !listo} onClick={enviar}>{directo ? "Confirmar" : "Enviar a Aprobación"}</button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

/**
 * Para que cada pantalla sean unas líneas: `const ap = useEnviarAprobacion(campusId);` … `ap.pedir({ kind, titulo, data, onDone })` … y `{ap.modal}`
 * en el JSX. Sin plantel no abre nada.
 */
export const useEnviarAprobacion = (campusId: number | null | undefined) => {
	const [p, setP] = useState<PedirAprobacion | null>(null);
	const modal = p && campusId ? (
		<EnviarAprobacion key={p.kind + JSON.stringify(p.data)} campusId={campusId} {...p} onCerrar={(r) => { const f = p.onCerrar; setP(null); f?.(r); }} />
	) : null;
	return { pedir: (x: PedirAprobacion) => setP(x), modal };
};

/**
 * ¿Sale el botón «Enviar a Aprobación»? Cuando el permiso fino no está claro, sale a quien tiene la sección de Pagos y el servidor decide
 * (`canDoDirect`, o un error con el motivo real en la vista previa).
 */
export const useTieneCaja = (): boolean => {
	const currentUser: any = useSelector((state: RootState) => state.userProfile.value);
	const p: any = getAccessPermissions(currentUser, "payments");
	return !!(p.create || p.update);
};
